import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, ArrowRight } from "lucide-react";
import axios from "axios";
import { CITIES, TICKET_2027, VARNA_2027 } from "@/lib/platform";
import { trackLead } from "@/lib/metaPixel";
import { goToTicketCheckout } from "./NationalNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CityLeadModal({ city, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", hp: "" });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const cityBg = city === "sofia" ? "София" : "Пловдив";

  const submit = async (e) => {
    e.preventDefault();
    if (status === "busy") return;
    if (!consent) {
      setStatus("error");
      setError("Моля, потвърди съгласието за обработка на данните.");
      return;
    }
    setStatus("busy");
    setError("");
    try {
      const r = await axios.post(`${API}/interest`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        city,
        source: "city-card",
        consent: true,
        hp: form.hp,
      });
      if (r.data?.success) {
        trackLead({ email: form.email, content_name: `Interest ${cityBg}`, content_category: "city-interest" });
        setStatus("done");
      } else {
        setStatus("error");
        setError("Провери полетата и опитай пак.");
      }
    } catch {
      setStatus("error");
      setError("Нещо се обърка. Опитай отново след малко.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="city-lead-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-[#050d1e]/75 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 50, opacity: 0, scale: 0.97 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 30, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-md w-full bg-[#0F284B] border border-[#1E3A63] rounded-2xl p-7 sm:p-9 text-[#F5EBD9]"
      >
        <button
          onClick={onClose}
          data-testid="city-lead-close"
          aria-label="Затвори"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center border border-[#F5EBD9]/20 rounded-full hover:border-[#F5892E] hover:text-[#F5892E] transition-colors"
        >
          <X className="w-4 h-4" strokeWidth={2} />
        </button>

        {status === "done" ? (
          <div className="text-center py-6" data-testid="city-lead-success">
            <CheckCircle2 className="w-12 h-12 text-[#F5892E] mx-auto mb-4" strokeWidth={1.8} />
            <h3 className="font-display uppercase text-2xl mb-2">Записан си!</h3>
            <p className="text-[#C8D4E6] text-sm leading-relaxed">
              Ще научиш пръв, когато обявим датата и билетите за {cityBg}.
            </p>
          </div>
        ) : (
          <>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F5892E] mb-2">
              {cityBg} · Очаквайте скоро
            </div>
            <h3 className="font-display uppercase text-2xl sm:text-3xl leading-tight mb-6">
              Научи първи за {cityBg}
            </h3>
            <form onSubmit={submit} className="space-y-4" data-testid="city-lead-form">
              <input
                type="text"
                required
                placeholder="Име"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="city-lead-name"
                className="w-full px-5 py-3.5 rounded-xl bg-[#0B1F3D] border border-[#1E3A63] text-[#F5EBD9] placeholder:text-[#8A9EB8] focus:outline-none focus:border-[#F5892E] transition-colors"
              />
              <input
                type="email"
                required
                placeholder="Имейл"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="city-lead-email"
                className="w-full px-5 py-3.5 rounded-xl bg-[#0B1F3D] border border-[#1E3A63] text-[#F5EBD9] placeholder:text-[#8A9EB8] focus:outline-none focus:border-[#F5892E] transition-colors"
              />
              <input
                type="tel"
                placeholder="Телефон (по желание)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="city-lead-phone"
                className="w-full px-5 py-3.5 rounded-xl bg-[#0B1F3D] border border-[#1E3A63] text-[#F5EBD9] placeholder:text-[#8A9EB8] focus:outline-none focus:border-[#F5892E] transition-colors"
              />
              {/* honeypot */}
              <input
                type="text"
                value={form.hp}
                onChange={(e) => setForm({ ...form, hp: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />
              <label className="flex items-start gap-3 text-xs text-[#8A9EB8] leading-relaxed cursor-pointer">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="city-lead-consent"
                  className="mt-0.5 w-4 h-4 accent-[#F5892E] shrink-0"
                />
                Съгласен/на съм данните ми да бъдат обработени, за да получа информация за събитието.
              </label>
              {status === "error" && (
                <div className="text-sm text-[#E5484D]" data-testid="city-lead-error">{error}</div>
              )}
              <button
                type="submit"
                disabled={status === "busy"}
                data-testid="city-lead-submit"
                className="w-full py-4 rounded-full bg-[#F5892E] text-[#0B1F3D] font-bold text-sm uppercase tracking-widest hover:bg-[#F5EBD9] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {status === "busy" ? "Записвам..." : "Научи първи"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
              </button>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

export default function CitiesSection() {
  const [modal, setModal] = useState(null);

  return (
    <section id="events" data-testid="cities-section" className="relative bg-[#0B1F3D] text-[#F5EBD9] py-24 lg:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
          className="mb-14 lg:mb-20"
        >
          <div className="font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5892E] mb-4 flex items-center gap-4">
            <span className="h-px w-10 bg-[#F5892E]" />
            Събитията
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight max-w-4xl">
            Три града. <span className="text-[#F5892E]">Хиляди възможности.</span>
          </h2>
          <p className="mt-6 text-lg text-[#C8D4E6] leading-relaxed max-w-[60ch]">
            „Играта на имотите" събира хората, които искат да разбират пазара, да вземат по-добри
            решения и да откриват нови възможности в недвижимите имоти.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {CITIES.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, delay: i * 0.12 }}
              data-testid={`city-card-${c.id}`}
              className={`group relative overflow-hidden rounded-2xl transition-all duration-500 ${
                c.next
                  ? "border-2 border-[#F5892E] shadow-[0_0_60px_-15px_rgba(245,137,46,0.5)] md:-translate-y-4"
                  : "border border-[#1E3A63]"
              }`}
            >
              {/* Image */}
              <div className="relative aspect-[3/4] sm:aspect-[4/5] overflow-hidden">
                <div
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
                  style={{ backgroundImage: `url(${c.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3D] via-[#0B1F3D]/40 to-transparent" />

                {c.next && (
                  <div className="absolute top-4 right-4 bg-[#F5892E] text-[#0B1F3D] font-mono text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 rounded-full">
                    Следващо събитие
                  </div>
                )}

                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-7">
                  <div className="font-display uppercase text-4xl sm:text-5xl tracking-tight leading-none">
                    {c.name}
                  </div>
                  <div className={`mt-2.5 font-mono text-[11px] uppercase tracking-[0.24em] ${c.next ? "text-[#F5892E]" : "text-[#F5EBD9]/70"}`}>
                    {c.upcoming ? "Очаквайте скоро" : VARNA_2027.date}
                  </div>
                  {c.next && (
                    <div className="mt-1.5 font-display uppercase text-xl text-[#F5EBD9]">
                      Early Bird — {TICKET_2027.price} €
                    </div>
                  )}
                  <button
                    onClick={() => (c.upcoming ? setModal(c.id) : goToTicketCheckout())}
                    data-testid={`city-cta-${c.id}`}
                    className={`mt-5 inline-flex items-center gap-2.5 px-6 py-3 rounded-full text-xs sm:text-sm font-bold uppercase tracking-widest transition-all ${
                      c.next
                        ? "bg-[#F5892E] text-[#0B1F3D] hover:bg-[#F5EBD9]"
                        : "border border-[#F5EBD9]/40 text-[#F5EBD9] hover:bg-[#F5EBD9] hover:text-[#0B1F3D]"
                    }`}
                  >
                    {c.upcoming ? "Научи първи" : "Купи билет"}
                    <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {modal && <CityLeadModal city={modal} onClose={() => setModal(null)} />}
      </AnimatePresence>
    </section>
  );
}
